/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import {
  EuiBasicTable,
  EuiLoadingChart,
  EuiTableComputedColumnType,
} from "@inscopix/ideas-eui";
import { captureException } from "@sentry/react";
import { CallOutError } from "components/CallOutError/CallOutError";
import {
  Metadatum,
  MetadataValue,
  File as DrsFile,
  useGetFileMetadataByFileIdQuery,
} from "graphql/_Types";
import { merge } from "lodash";
import { DATA_TABLE_TOOLTIP_ID } from "pages/gdt/layout/DataTableTooltip";
import { ButtonIconPlain } from "pages/gdt/plain/ButtonIconPlain";
import { JsonValue } from "type-fest";
import { FILE_TYPES_BY_ID } from "types/FileTypes";
import { metadataMap } from "types/MetadataMap";
import { isDefined } from "utils/isDefined";
import { isNonNull } from "utils/isNonNull";
import { isNonNullish } from "utils/isNonNullish";
import { stringifyMetadataValue } from "utils/stringifyMetadataValue";

type TMetadataDisplayKeyAndValues = {
  key: Metadatum["key"];
  displayName: string;
  value: MetadataValue["value"];
};

const styles = {
  displayName: css`
    display: inline-flex;
    align-items: center;

    /* hide the copy button until the cell is hovered */
    .metadata-key-copy-button {
      visibility: hidden;
      transition: visibility 0.1s linear;
    }

    /* show the copy button when the cell is hovered */
    &:hover .metadata-key-copy-button {
      visibility: visible;
    }
  `,
};

type FlyoutFileInfoBodyMetadataTableProps = {
  drsFile: Pick<DrsFile, "id" | "status" | "fileType">;
};

export const FlyoutFileInfoBodyMetadataTable = ({
  drsFile,
}: FlyoutFileInfoBodyMetadataTableProps) => {
  const fileTypeKey = isNonNullish(drsFile.fileType)
    ? FILE_TYPES_BY_ID[drsFile.fileType]?.key
    : undefined;

  const metadataFilter = isDefined(fileTypeKey)
    ? {
        metadataFilter: {
          metadatumByMetadataId: {
            key: {
              in: Object.keys(metadataMap[fileTypeKey] ?? {}),
            },
          },
        },
      }
    : undefined;

  const { data, loading } = useGetFileMetadataByFileIdQuery({
    fetchPolicy: "cache-and-network",
    variables: {
      drsFileId: drsFile.id,
      ...metadataFilter,
    },
    onError: (err) => captureException(err),
  });
  const columns: Array<
    EuiTableComputedColumnType<TMetadataDisplayKeyAndValues>
  > = [
    {
      render: (data) => {
        return (
          <span css={styles.displayName}>
            {data.displayName}
            <ButtonIconPlain
              className={"metadata-key-copy-button"}
              color={"text"}
              onClick={() => {
                void navigator.clipboard
                  .writeText(String(data.key))
                  .catch(() => {
                    captureException("Failed to write to clipboard");
                  });
              }}
              iconType="key"
              iconSize="s"
              aria-label="Copy metadatum key"
              data-tooltip-id={DATA_TABLE_TOOLTIP_ID}
              data-tooltip-html={"Copy metadatum key: " + data.key}
            />
          </span>
        );
      },
      name: "",
      width: "50%",
    },
    {
      render: (data) => data.value,
      name: "",
      align: "right",
    },
  ];

  const metadata = data?.drsFile?.metadata.nodes
    .map(({ metadatum }) => metadatum)
    .filter(isNonNull);

  // get file metadata
  const metadataForFileType = isNonNullish(fileTypeKey)
    ? metadataMap[fileTypeKey]
    : undefined;

  if (
    // check for query error
    metadata === undefined
  ) {
    if (loading) {
      return <EuiLoadingChart />;
    }
    return <CallOutError />;
  }

  // series parent metadata if exists
  const seriesParentMetadataByKey =
    data?.drsFile?.seriesParentFile?.metadata.nodes
      .map(({ metadatum }) => metadatum)
      .filter(isNonNull)
      .reduce<Record<Metadatum["key"], JsonValue>>(
        (metadataByKey, { key, activeValue }) => {
          metadataByKey[key] = activeValue;
          return metadataByKey;
        },
        {},
      );

  // active file metadata
  const metadataByKey = metadata.reduce<Record<Metadatum["key"], JsonValue>>(
    (metadataByKey, { key, activeValue }) => {
      metadataByKey[key] = activeValue;
      return metadataByKey;
    },
    {},
  );

  // combined metadata
  // inherit series parent metadata and overwrite with child values if exist
  const combinedMetadataByKey = merge(seriesParentMetadataByKey, metadataByKey);

  const metadataDisplayKeyAndValues: TMetadataDisplayKeyAndValues[] = [];

  if (metadataForFileType !== undefined) {
    for (const [metadataMapKey, metadataMapDisplayName] of Object.entries(
      metadataForFileType,
    )) {
      const metadataValue = combinedMetadataByKey[metadataMapKey];

      if (
        isNonNullish(metadataValue) &&
        !(Array.isArray(metadataValue) && metadataValue.length === 0)
      ) {
        metadataDisplayKeyAndValues.push({
          key: metadataMapKey,
          displayName: metadataMapDisplayName,
          value: stringifyMetadataValue(metadataValue),
        });
      }
    }
  } else if (Array.isArray(metadata)) {
    // if no file type metadata map, show all metadata key/values
    for (const metadatum of metadata) {
      metadataDisplayKeyAndValues.push({
        key: metadatum.key,
        displayName:
          metadatum.displayName !== "" ? metadatum.displayName : metadatum.key,
        value: stringifyMetadataValue(metadatum.activeValue),
      });
    }
  }

  return (
    <EuiBasicTable
      responsive={false}
      items={metadataDisplayKeyAndValues}
      rowHeader="displayName"
      columns={columns}
      compressed
      tableLayout="auto"
    />
  );
};
